import { Typography } from '@mui/material';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { ActionButton, Dialog } from 'app/components';
import { TranslationKeys } from 'app/translations';
import type { EmployerDataFilter, ReportFilterResult } from 'app/types';

import { ListItemStyled, UnorderedListStyled } from './UserAccessesListConfirmDialog.styles';
import type { UserAccessesListSelectedData } from '../../UserAccessesList.types';

type UserAccessesListConfirmDialogProps = {
  show: boolean;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedData: UserAccessesListSelectedData;
  results: Array<ReportFilterResult> | undefined;
};

const getEmployerNames = (employers: Array<EmployerDataFilter>, employerNames: Map<string, string>) => {
  employers.forEach(employer => {
    employerNames.set(employer.employerId, employer.name);
    getEmployerNames(employer.subEmployers, employerNames);
  });
};

export const UserAccessesListConfirmDialog = ({
  show,
  isLoading,
  onConfirm,
  onClose,
  selectedData,
  results,
}: UserAccessesListConfirmDialogProps) => {
  const employerNames = new Map<string, string>();
  getEmployerNames(
    results?.flatMap(result => ('subOwnerId' in result ? result.employers : [result])) || [],
    employerNames,
  );
  const selectedEmployerNames = selectedData.employerIds
    .map(employerId => employerNames.get(employerId))
    .filter((employerName): employerName is string => !!employerName);
  const unavailableEmployerCount = selectedData.employerIds.length - selectedEmployerNames.length;

  return (
    <Dialog
      open={show}
      maxWidth={'md'}
      title={<FormattedMessage id={TranslationKeys.users_accessesList_confirmDialog_title} />}
      onClose={onClose}
      actions={
        <>
          <ActionButton messageId={TranslationKeys.no} disabled={isLoading} onClick={onClose} variant={'outlined'} />
          <ActionButton messageId={TranslationKeys.yes} disabled={isLoading} onClick={onConfirm} />
        </>
      }
      fullWidth
    >
      <Typography>
        <FormattedMessage id={TranslationKeys.users_accessesList_confirmDialog_header} />
      </Typography>
      {selectedData.allEmployers ? (
        <Typography variant={'body2'} marginTop={2}>
          <FormattedMessage id={TranslationKeys.users_accessesList_confirmDialog_accessToAllEmployers} />
        </Typography>
      ) : (
        <UnorderedListStyled>
          {selectedData.employerIds.length ? (
            <>
              {selectedEmployerNames.map(employerName => (
                <ListItemStyled key={employerName}>
                  <Typography>{employerName}</Typography>
                </ListItemStyled>
              ))}
              {!!unavailableEmployerCount && (
                <ListItemStyled>
                  <Typography>
                    <FormattedMessage
                      id={TranslationKeys.users_accessesList_confirmDialog_andMore}
                      values={{ count: unavailableEmployerCount }}
                    />
                  </Typography>
                </ListItemStyled>
              )}
            </>
          ) : (
            <Typography variant={'caption'} textTransform={'initial'}>
              <FormattedMessage id={TranslationKeys.users_accessesList_confirmDialog_noAccesses} />
            </Typography>
          )}
        </UnorderedListStyled>
      )}
    </Dialog>
  );
};
